import type { MissingCustomerField } from './customerCheckout';
import type { PosItemError } from './validatePosItems';

/** Một phần tử `errorFields` của antd `form.validateFields()`. */
export interface FormErrorField {
  name: Array<string | number>;
  errors: string[];
}

export interface CheckoutProblemInput {
  /** Số dòng hàng trong giỏ. */
  itemCount: number;
  /** Kết quả `validatePosItems` — lỗi theo từng dòng hàng. */
  itemErrors: Record<string, PosItemError>;
  /** `errorFields` từ `form.validateFields()` — gồm cả lỗi định dạng. */
  errorFields: FormErrorField[];
  /** Trường bắt buộc còn trống theo `missingCustomerFields`. */
  missing: MissingCustomerField[];
  /** Đã gắn khách vào đơn chưa (`customer_phone` có giá trị). */
  hasCustomer: boolean;
}

/**
 * Gộp mọi lý do không ghi được hoá đơn thành một danh sách để đổ vào toast.
 *
 * Thứ tự ưu tiên: giỏ hàng → dòng hàng → lỗi field do antd bắt → trường trống mà
 * antd chưa bắt được. Message của antd được ưu tiên hơn nhãn tĩnh vì nó nói rõ
 * lỗi định dạng ("CCCD phải gồm 12 chữ số") thay vì chỉ "còn trống"; một field
 * đã có message của antd thì không liệt kê lại nhãn tĩnh của nó.
 *
 * Trường bắt buộc nằm trong CustomerDialog lazy-mount: nếu dialog chưa mount thì
 * `validateFields()` bỏ qua chúng — nhánh "nhãn tĩnh" là lưới an toàn để toast
 * không bao giờ rỗng khi thực sự còn thiếu.
 */
export function buildCheckoutProblems({
  itemCount,
  itemErrors,
  errorFields,
  missing,
  hasCustomer,
}: CheckoutProblemInput): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  const push = (msg: string) => {
    if (!msg || seen.has(msg)) return;
    seen.add(msg);
    problems.push(msg);
  };

  if (itemCount === 0) push('Chưa có sản phẩm trong giỏ');

  const rowErrors = Object.values(itemErrors);
  if (rowErrors.some((e) => e.seller)) push('Có dòng hàng chưa chọn nhân viên bán');
  if (rowErrors.some((e) => e.warehousePick)) push('Có dòng hàng chưa chọn kho xuất hàng');

  // Chưa gắn khách thì toàn bộ trường của khách đương nhiên trống — liệt kê cả 9
  // dòng chỉ gây nhiễu. Gộp thành một dòng, khớp với card đỏ ở sidebar.
  if (!hasCustomer) {
    push('Chưa chọn khách hàng');
    return problems;
  }

  const fieldsWithAntdError = new Set<string>();
  for (const f of errorFields) {
    fieldsWithAntdError.add(String(f.name[0]));
    if (f.errors[0]) push(f.errors[0]);
  }

  for (const m of missing) {
    if (!fieldsWithAntdError.has(m.name)) push(m.label);
  }

  return problems;
}
